import React, { useRef } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import WorkListPdfModalSelect from './WorkListPdfModalSelect';
import WorkListPdfModalIndexing, {
  type WorkListPdfModalIndexingHandle,
} from './WorkListPdfModalIndexing';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { useExportStationWorkStore } from '../../shared/store/exportStationWorkStore';
import deleteIcon from '../../asset/icon/delete.png';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { WORK_LIST_EXPORT_MAX_ROWS } from '../../shared/constants/limits';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';

interface WorkListPdfModalSelectAndIndexingProps {
  onCloseModal: () => void;
  onNextStep: () => void;
}

/**
 * PDF 가져오기: 좌측 작품 선택 + 우측 순서 지정을 한 화면에 둔다.
 */
const WorkListPdfModalSelectAndIndexing: React.FC<WorkListPdfModalSelectAndIndexingProps> = ({
  onCloseModal,
  onNextStep,
}) => {
  const { selectedDataList, setSelectedDataList } = useExportStationWorkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const indexingRef = useRef<WorkListPdfModalIndexingHandle>(null);

  const handleNext = () => {
    if (selectedDataList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_WORKS_EMPTY'),
      });
      return;
    }
    if (selectedDataList.length > WORK_LIST_EXPORT_MAX_ROWS) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('WORK_LIST_EXPORT_MAX_ITEMS', {
          maxCount: WORK_LIST_EXPORT_MAX_ROWS,
        }),
      });
      return;
    }
    const fromIndexing = indexingRef.current?.getOrderedList();
    const ordered =
      fromIndexing != null && fromIndexing.length === selectedDataList.length
        ? fromIndexing
        : selectedDataList;
    setSelectedDataList(ordered);
    onNextStep();
  };

  return (
    <MainContainer>
      <HeaderRow>
        <Title>
          {getModalMessage('WORK_LIST_PDF_SELECT_TITLE')} (
          {getModalMessage('WORK_LIST_PDF_SELECTED_COUNT', {
            count: selectedDataList.length,
          })}
          )
        </Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={onCloseModal}
        />
      </HeaderRow>
      <ContentContainer>
        <WorkListPdfModalSelect />
        <WorkListPdfModalIndexing ref={indexingRef} />
      </ContentContainer>
      <ButtonContainer>
        <HeadlessBtn value={getBtnName('NEXT_STEP')} handleClick={handleNext} StyledBtn={PaperBlurFitBtn} />
      </ButtonContainer>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: calc(90vh - 40px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Title = styled.div`
  min-width: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  max-height: 75vh;
  gap: 3vh;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default WorkListPdfModalSelectAndIndexing;
